import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronRight, File, Folder, FolderOpen } from 'lucide-react';
import { backend } from '@/api/backend';
import { ApiError } from '@/api/http';
import { explainLine } from '@/api/explain';
import { Button, Skeleton } from '@/components/ui';
import { formatBytes } from '@/lib/format';
import { cn } from '@/lib/cn';
import { fileName, groupManifest, readManifestPage, type FolderNode, type ManifestEntry } from './manifestTree';

/**
 * Every file this package would hand over, openable.
 *
 * The folder tree beside this comes from the sealed walk and is a projection.
 * This reads the delivery manifest, the same derivation the executor copies
 * from, so what is opened here is what lands in the buyer's bucket.
 *
 * Paged rather than loaded whole: a large run's tree does not belong in one
 * response. The cursor is present only when a page filled, so an absent cursor
 * is the end of the tree rather than an empty page fetched to discover it, and
 * the count on screen says how much has been read so far rather than implying
 * it is all of it.
 */

const PAGE = 200;

function Row({ node, depth }: { node: FolderNode; depth: number }) {
  const [open, setOpen] = useState(depth < 1);
  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 border-b border-line px-4 py-1.5 text-left transition-colors duration-fast hover:bg-hov"
        style={{ paddingLeft: 16 + depth * 18 }}
      >
        <ChevronRight
          className={cn('h-3.5 w-3.5 flex-none text-ink-4 transition-transform', open && 'rotate-90')}
          aria-hidden="true"
        />
        {open ? (
          <FolderOpen className="h-3.5 w-3.5 flex-none text-ink-4" aria-hidden="true" />
        ) : (
          <Folder className="h-3.5 w-3.5 flex-none text-ink-4" aria-hidden="true" />
        )}
        <span className="mono min-w-0 flex-1 truncate text-small text-ink" title={node.path}>
          {node.name}
        </span>
        <span className="mono w-24 flex-none text-right text-caption text-ink-2">
          {node.objects.toLocaleString()} files
        </span>
        <span className="mono w-20 flex-none text-right text-caption text-ink-3">{formatBytes(node.bytes)}</span>
      </button>
      {open && (
        <>
          {node.folders.map((f) => (
            <Row key={f.path} node={f} depth={depth + 1} />
          ))}
          {node.files.map((f) => (
            <FileRow key={f.manifestPath} entry={f} depth={depth + 1} />
          ))}
        </>
      )}
    </>
  );
}

function FileRow({ entry, depth }: { entry: ManifestEntry; depth: number }) {
  return (
    <div
      className="flex items-center gap-2 border-b border-line px-4 py-1.5 last:border-0"
      style={{ paddingLeft: 16 + depth * 18 }}
    >
      <span className="w-3.5 flex-none" />
      <File className="h-3.5 w-3.5 flex-none text-ink-4" aria-hidden="true" />
      <span className="mono min-w-0 flex-1 truncate text-small text-ink" title={entry.manifestPath}>
        {fileName(entry)}
      </span>
      {/* Where the bytes come from. A buyer sees the left column; this is the
          column that proves it is the file it claims to be. */}
      <span
        className="mono hidden min-w-0 max-w-[24rem] flex-1 truncate text-caption text-ink-4 lg:block"
        title={entry.sourceObjectKey}
      >
        {entry.sourceObjectKey}
      </span>
      <span className="mono w-20 flex-none text-right text-caption text-ink-3">{formatBytes(entry.byteTotal)}</span>
    </div>
  );
}

export function DeliveredFiles({ runId }: { runId: string }) {
  const q = useInfiniteQuery({
    queryKey: ['run-manifest', runId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      backend.getPackagingRunManifest(runId, { pageSize: PAGE, afterManifestPath: pageParam }, signal),
    // An absent cursor is the end of the tree. Reading it as "fetch again"
    // would loop on the last page forever.
    getNextPageParam: (last) => readManifestPage(last)?.nextAfterManifestPath,
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 30_000,
  });

  if (q.isPending) return <Skeleton className="mx-4 my-3 h-5" />;
  if (q.isError) {
    return <p className="px-4 py-3 text-small text-warn">{explainLine(q.error, 'read the delivered files')}</p>;
  }

  const pages = (q.data?.pages ?? []).map(readManifestPage);
  // A page that does not parse is drift, and drawing zero files for it would
  // read exactly like an empty package, which means the opposite.
  if (pages.some((p) => p === null)) {
    return (
      <p className="px-4 py-3 text-small text-warn">
        The manifest came back in a shape this screen does not recognise. Nothing is drawn, because any count here would be
        a guess.
      </p>
    );
  }

  const entries = pages.flatMap((p) => p!.entries);
  const root = groupManifest(entries);

  if (entries.length === 0) {
    return (
      <p className="px-4 py-3 text-small text-warn">
        This package is empty. No file has been placed in it, so a delivery has nothing to copy and would be refused.
        The folders above are what the walk found, which is a different thing from what curation placed.
      </p>
    );
  }

  return (
    <>
      {root.folders.map((f) => (
        <Row key={f.path} node={f} depth={0} />
      ))}
      {root.files.map((f) => (
        <FileRow key={f.manifestPath} entry={f} depth={0} />
      ))}
      <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-2.5">
        {q.hasNextPage && (
          <Button variant="ghost" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {q.isFetchingNextPage ? 'Reading…' : `Load the next ${PAGE} files`}
          </Button>
        )}
        <span className="text-caption text-ink-3">
          {q.hasNextPage
            ? `${entries.length.toLocaleString()} files read so far, and there are more. This is the delivery manifest, so every file here is a file that ships.`
            : `${entries.length.toLocaleString()} files, ${formatBytes(root.bytes)}. That is the whole package: this is the same list the delivery copies from, so nothing else ships and nothing here is left behind.`}
        </span>
      </div>
    </>
  );
}
